import { createSelector } from 'reselect'
import { State } from 'interfaces/state'
import { AdvisorUid, RawAdvisorReview } from 'components/features/coaching/advisor/types/advisor'
import { getItemsFromCollection, getItemFromCollection, getItemsByKey } from 'helpers/normalizeHelpers'
import { createOwnPropsSelector } from 'selectors/globalSelectors'
import { Meeting, MeetingUid } from 'components/features/coaching/advisorPage/types/interfaces'
import find from 'lodash/find'
import { ReviewRequest } from 'components/features/coaching/advisorStore/interfaces/state'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import { floatingPanesCollectionSelector } from 'selectors/floatingPanesSelectors'
import { AdvisorFloatPane } from 'components/features/coaching/advisorMeeting/types/floatPane'
import { AdvisorMeetingWizardFields } from 'components/features/coaching/advisorMeeting/types/requests'


export const advisorsSelector = (state: State) => state.data.advisors
const advisorsUidSelector = (_: State, props: { uid: AdvisorUid }) => props.uid

export const advisorsCollectionSelector = createSelector(
  advisorsSelector,
  (data) => data.advisors
)

export const meetingsSelector = createSelector(
  advisorsSelector,
  (advisors) => advisors.meetings.data
)

export const meetingSelector = createSelector(
  meetingsSelector,
  createOwnPropsSelector<MeetingUid>(),
  (meetings, uid) => {
    if (!meetings) {
      return null
    }
    return getItemFromCollection<Meeting, 'uid'>(meetings, uid)
  }
)

export const nextScheduledMeetingSelector = createSelector(
  meetingsSelector,
  (meetings) => {
    const nextMeetingUid = meetings?.allKeys.find((uid) => {
      const meeting = meetings.byKey[uid]
      return meeting && meeting.status === 'scheduled'
    })
    return nextMeetingUid ? meetings?.byKey[nextMeetingUid] : null
  }
)

export const advisorsSearchResultsSelector = createSelector(
  advisorsSelector,
  (data) => data.advisorsPagination?.results
)

export const advisorsVisibleSelector = (state: State) => state.app.advisors.advisorsVisible

export const createAdvisorSelector = () => createSelector(
  advisorsCollectionSelector,
  advisorsUidSelector,
  (collection, uid) => {
    if (!collection) {
      return null
    }

    return collection.byKey[uid] ?? null
  }
)

export const createTimeslotSelector = () => createSelector(
  advisorsSelector,
  advisorsUidSelector,
  ({ timeslots }, uid) => {
    const advisorTimeslots = timeslots[uid]
    if (!advisorTimeslots || advisorTimeslots.allKeys.length === 0) {
      return null
    }
    return getItemsFromCollection(advisorTimeslots)
  }
)

const usernamePropSelector = (_: State, param: { username: string }) => param.username

const reviewsPaginationSelector = createSelector(
  advisorsSelector,
  (advisors) => advisors.reviewsPagination
)

const reviewsSelector = createSelector(
  advisorsSelector,
  (advisors) => advisors.reviews
)

export const requestByUsernameSelector = createSelector(
  reviewsPaginationSelector,
  usernamePropSelector,
  (reviewsPagination, advisor_username): ReviewRequest | null => {
    return find(reviewsPagination, { advisor_username }) || null
  }
)

export const reviewsByUsernameSelector = createSelector(
  reviewsSelector,
  requestByUsernameSelector,
  (reviews, request) => {
    if (request?.results) {
      return getItemsByKey(reviews.byKey, request.results)
    }
    return null
  }
)

export const requestByMeetingUidSelector = createSelector(
  reviewsPaginationSelector,
  (_: State, param: { uid: MeetingUid }) => param.uid,
  (reviewsPagination, meeting_uid): ReviewRequest | null => {
    return find(reviewsPagination, { meeting_uid }) || null
  }
)

export const reviewByMeetingUidSelector = createSelector(
  reviewsSelector,
  (_: State, param: { uid: MeetingUid }) => param.uid,
  (reviews, uid): RawAdvisorReview | null => {
    const arr = getItemsFromCollection(reviews)
    return find(arr, { meeting_uid: uid }) || null
  }
)

export function advisorFloatingPaneStateOrNullSelector (store: State): AdvisorFloatPane | null {
  return (floatingPanesCollectionSelector(store).byKey.advisor as AdvisorFloatPane) || null
}

export function advisorFloatingPaneFieldsOrNullSelector (store: State): AdvisorMeetingWizardFields | null {
  const advisorFloatingPaneState = advisorFloatingPaneStateOrNullSelector(store)

  return (advisorFloatingPaneState as {
    fields?: AdvisorMeetingWizardFields
  })?.fields || null
}

export const whetherToSkipSignUpStepSelector = createSelector(
  advisorFloatingPaneFieldsOrNullSelector,
  isAuthenticatedSelector,
  (
    advisorFloatingPaneFieldsOrNull: AdvisorMeetingWizardFields | null,
    isUserAuthentificated: boolean,
  ): boolean => {
    if (!advisorFloatingPaneFieldsOrNull || !isUserAuthentificated) {
      return false
    }

    if (advisorFloatingPaneFieldsOrNull.flow === 'regular') {
      return true
    }
    return !!advisorFloatingPaneFieldsOrNull.isSignUpStepSucceed
  }
)

export const advisorsRequestSelector = createSelector(
  advisorsSelector,
  (advisors) => advisors.advisorsRequest
)
